import { products } from "@wix/stores";
import { readOnlyVariantsV3 } from '@wix/stores';


/**
 * @typedef IVariation
 * @type {object}
 * @property {string} currency
 * @property {number} discountedPrice
 * @property {string} formattedDiscountedPrice
 * @property {string} formattedPrice
 * @property {string} formattedPricePerUnit
 * @property {number} price
 * @property {number} pricePerUnit
 * @property {string} sku
 * @property {boolean} visible
 * @property {number} weight
 */

/**
 * @typedef IVariationItem
 * @type {object}
 * @property {string} _id
 * @property {object} choices
 * @property {IVariation} variant
 */

export class ProductVariationRepository {
    constructor() {}

    async getVariationItems(args) {
        const res = await products.queryProductVariants(args.productId);

        const transformedVariants = await this._transformVariants_V1(res.variants)
        return transformedVariants

    }

    async getVariationItems_V3(args){
        
        const res = await readOnlyVariantsV3
            .queryVariants({
                fields: ['CURRENCY']
            })
            .eq("productData.productId", args.productId)
            .find();

        const transformedVariants = await this._transformVariants_V3(res.items)
        return transformedVariants
        
    }


    async _transformVariants_V1(items){
        return items.map(item => {
            const priceData = item.variant.priceData;

            return {
                _id: item._id,
                choices: item.choices,
                variant: {
                    currency: priceData.currency,
                    price: priceData.price,
                    discountedPrice: priceData.discountedPrice,
                    formattedPrice: priceData.formatted.price,
                    formattedDiscountedPrice: priceData.formatted.discountedPrice,
                    weight: item.variant.weight,
                    sku: item.variant.sku,
                    visible: item.variant.visible
                }
            };
        });
    }


    async _transformVariants_V3(items) {
       return items.map(item => {
            const choices = {};
            item.optionChoices?.forEach(choice => {
            const { optionName, choiceName } = choice.optionChoiceNames;
            choices[optionName] = choiceName;
            });

            const priceAmount = parseFloat(item.price?.actualPrice?.amount ?? 0);
            const formatted = item.price?.actualPrice?.formattedAmount ?? "$0";

            return {
            _id: item._id,
            choices,
            variant: {
                currency: item.productData?.currency ?? "USD",
                price: priceAmount,
                discountedPrice: priceAmount,
                formattedPrice: formatted,
                formattedDiscountedPrice: formatted,
                weight: item.productData?.physicalProperties?.weight ?? 0,
                sku: item.sku ?? "", 
                visible: item.visible
            }
            };
        });
    }

    getDummyVariationItems() {
        return [{
                _id: "1",
                choices: {
                    color: "white",
                    size: "S",
                },
                variant: {
                    currency: "USD",
                    discountedPrice: 10,
                    formattedDiscountedPrice: "$10.00",
                    formattedPrice: "$10.00",
                    formattedPricePerUnit: "$10.00",
                    price: 10,
                    pricePerUnit: 10,
                    sku: "1",
                    visible: true,
                    weight: 0,
                },
            },
            {
                _id: "2",
                choices: {
                    color: "white",
                    size: "M",
                },
                variant: {
                    currency: "USD",
                    discountedPrice: 20,
                    formattedDiscountedPrice: "$20.00",
                    formattedPrice: "$20.00",
                    formattedPricePerUnit: "$20.00",
                    price: 20,
                    pricePerUnit: 20,
                    sku: "2",
                    visible: true,
                    weight: 0,
                },
            },
            {
                _id: "3",
                choices: {
                    color: "white",
                    size: "L",
                },
                variant: {
                    currency: "USD",
                    discountedPrice: 30,
                    formattedDiscountedPrice: "$30.00",
                    formattedPrice: "$30.00",
                    formattedPricePerUnit: "$30.00",
                    price: 30,
                    pricePerUnit: 30,
                    sku: "3",
                    visible: true,
                    weight: 0,
                },
            },
            {
                _id: "4",
                choices: {
                    color: "black",
                    size: "S",
                },
                variant: {
                    currency: "USD",
                    discountedPrice: 40,
                    formattedDiscountedPrice: "$40.00",
                    formattedPrice: "$40.00",
                    formattedPricePerUnit: "$40.00",
                    price: 40,
                    pricePerUnit: 40,
                    sku: "4",
                    visible: true,
                    weight: 0,
                },
            },
            {
                _id: "5",
                choices: {
                    color: "black",
                    size: "M",
                },
                variant: {
                    currency: "USD",
                    discountedPrice: 50,
                    formattedDiscountedPrice: "$50.00",
                    formattedPrice: "$50.00",
                    formattedPricePerUnit: "$50.00",
                    price: 50,
                    pricePerUnit: 50,
                    sku: "5",
                    visible: true,
                    weight: 0,
                },
            },
            {
                _id: "6",
                choices: {
                    color: "black",
                    size: "L",
                },
                variant: {
                    currency: "USD",
                    discountedPrice: 60,
                    formattedDiscountedPrice: "$60.00",
                    formattedPrice: "$60.00",
                    formattedPricePerUnit: "$60.00",
                    price: 60,
                    pricePerUnit: 60,
                    sku: "6",
                    visible: true,
                    weight: 0,
                },
            },
        ];
    }

    getDummyVariationItems_V3(){
        return [
            {
                "_id": "1",
                "optionChoices": [
                    {
                        "optionChoiceNames": {
                            "optionName": "Size",
                            "choiceName": "Large"
                        }
                    },
                    {
                        "optionChoiceNames": {
                            "optionName": "Color",
                            "choiceName": "Red"
                        }
                    }
                ],
                "price": {
                    "actualPrice": {
                        "amount": 25.00,
                        "formattedAmount": "$25.00"
                    },
                    "compareAtPrice": {
                        "amount": 30.00,
                        "formattedAmount": "$30.00"
                    }
                },
                "sku": "1",
                "visible": true,
                "physicalProperties": {
                    "weight": 1.5,
                    "pricePerUnit": {
                        "value": 10,
                        "description": "10 per unit"
                    }
                }
            },
            {
                "_id": "2",
                "optionChoices": [
                    {
                        "optionChoiceNames": {
                            "optionName": "Size",
                            "choiceName": "Medium"
                        }
                    },
                    {
                        "optionChoiceNames": {
                            "optionName": "Color",
                            "choiceName": "Blue"
                        }
                    }
                ],
                "price": {
                    "actualPrice": {
                        "amount": 22.50,
                        "formattedAmount": "$22.50"
                    },
                    "compareAtPrice": {
                        "amount": 28.00,
                        "formattedAmount": "$28.00"
                    }
                },
                "sku": "2",
                "visible": true,
                "physicalProperties": {
                    "weight": 1.2,
                    "pricePerUnit": {
                        "value": 8,
                        "description": "8 per unit"
                    }
                }
            },
            {
                "_id": "3",
                "optionChoices": [
                    {
                        "optionChoiceNames": {
                            "optionName": "Size",
                            "choiceName": "Small"
                        }
                    },
                    {
                        "optionChoiceNames": {
                            "optionName": "Color",
                            "choiceName": "Green"
                        }
                    }
                ],
                "price": {
                    "actualPrice": {
                        "amount": 20.00,
                        "formattedAmount": "$20.00"
                    },
                    "compareAtPrice": {
                        "amount": 25.00,
                        "formattedAmount": "$25.00"
                    }
                },
                "sku": "3",
                "visible": true,
                "physicalProperties": {
                    "weight": 1.0,
                    "pricePerUnit": {
                        "value": 6,
                        "description": "6 per unit"
                    }
                }
            },
            {
                "_id": "4",
                "optionChoices": [
                    {
                        "optionChoiceNames": {
                            "optionName": "Size",
                            "choiceName": "Large"
                        }
                    },
                    {
                        "optionChoiceNames": {
                            "optionName": "Color",
                            "choiceName": "Black"
                        }
                    }
                ],
                "price": {
                    "actualPrice": {
                        "amount": 27.00,
                        "formattedAmount": "$27.00"
                    },
                    "compareAtPrice": {
                        "amount": 32.00,
                        "formattedAmount": "$32.00"
                    }
                },
                "sku": "4",
                "visible": true,
                "physicalProperties": {
                    "weight": 1.6,
                    "pricePerUnit": {
                        "value": 12,
                        "description": "12 per unit"
                    }
                }
            },
            {
                "_id": "5",
                "optionChoices": [
                    {
                        "optionChoiceNames": {
                            "optionName": "Size",
                            "choiceName": "Medium"
                        }
                    },
                    {
                        "optionChoiceNames": {
                            "optionName": "Color",
                            "choiceName": "White"
                        }
                    }
                ],
                "price": {
                    "actualPrice": {
                        "amount": 23.75,
                        "formattedAmount": "$23.75"
                    },
                    "compareAtPrice": {
                        "amount": 29.00,
                        "formattedAmount": "$29.00"
                    }
                },
                "sku": "5",
                "visible": true,
                "physicalProperties": {
                    "weight": 1.3,
                    "pricePerUnit": {
                        "value": 9,
                        "description": "9 per unit"
                    }
                }
            },
            {
                "_id": "6",
                "optionChoices": [
                    {
                        "optionChoiceNames": {
                            "optionName": "Size",
                            "choiceName": "Small"
                        }
                    },
                    {
                        "optionChoiceNames": {
                            "optionName": "Color",
                            "choiceName": "Yellow"
                        }
                    }
                ],
                "price": {
                    "actualPrice": {
                        "amount": 19.50,
                        "formattedAmount": "$19.50"
                    },
                    "compareAtPrice": {
                        "amount": 24.00,
                        "formattedAmount": "$24.00"
                    }
                },
                "sku": "6",
                "visible": true,
                "physicalProperties": {
                    "weight": 0.9,
                    "pricePerUnit": {
                        "value": 5,
                        "description": "5 per unit"
                    }
                }
            }
        ]
    }
}